import React from 'react';
import { Dialog, DialogPanel, DialogTitle, Description, DialogBackdrop, Transition } from '@headlessui/react'
import { X } from 'lucide-react'
import { Fragment } from 'react';

export function ModalContainer({
    isOpen,
    onClose,
    children,
    title,
    description,
    headerLeft,
    headerRight,
    size = 'md', // sm, md, lg, xl, full
    showClose = true,
    hideHeaderBorder = false,
    className = ''
}) {
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        '5xl': 'max-w-5xl',
        '7xl': 'max-w-7xl',
        full: 'max-w-[95%]'
    }

    return (
        <Dialog
            as="div"
            className="relative max-w- z-[10002]"
            open={isOpen}
            onClose={onClose}
        >
            {/* Background overlay */}
            <DialogBackdrop className="fixed inset-0 bg-white/80" />
            {/* Modal position */}
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-2 sm:p-4">

                    {/* Modal panel */}
                    <DialogPanel
                        className={`w-full ${sizeClasses[size]} overflow-hidden rounded-2xl 
                        bg-white p-3 sm:p-4 md:p-6 text-left align-middle shadow-lg border border-gray-300 relative ${className}`}
                    >
                        {/* Close button - positioned at top right corner */}
                        {showClose && (
                            <button
                                onClick={onClose}
                                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:text-gray-700 hover:bg-gray-200 focus:outline-none z-10"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        )}
                        
                        {/* Header */}
                        {(headerLeft || title || headerRight) && (
                            <div className={`flex justify-between items-start mb-4 ${hideHeaderBorder ? '' : 'border-b border-gray-100 pb-2'}`}>
                                {/* Left side */}
                                {headerLeft || (
                                    <div>
                                        {title && (
                                            <DialogTitle
                                                as="h3"
                                                className="text-[21px] font-semibold leading-7 text-gray-900"
                                            >
                                                {title}
                                            </DialogTitle>
                                        )}
                                        {description && (
                                            <Description className="mt-1 text-sm text-gray-500">
                                                {description}
                                            </Description>
                                        )}
                                    </div>
                                )}

                                {/* Right side */}
                                {headerRight && (
                                    <div className="flex items-center gap-4">
                                        {headerRight}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Content */}
                        <div className={(headerLeft || title || headerRight) ? "mt-2" : "mt-0"}>
                            {children}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}

export function ModalContainerFull({
    isOpen,
    onClose,
    children,
    showClose = true,
    className = '',
    size = 'lg'
}) {
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        '5xl': 'max-w-5xl',
        '7xl': 'max-w-7xl',
        full: 'max-w-[95%]'
    }

    return (
        <Dialog
            as="div"
            transition
            className="relative z-[10002] transition duration-300 ease-out data-[closed]:opacity-0"
            open={isOpen}
            onClose={onClose}
        >
            <DialogBackdrop className="fixed inset-0 bg-white/80" />

            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        className={`w-full h-full ${sizeClasses[size]} transform overflow-hidden rounded-xl 
                        bg-white text-left align-middle shadow-xl transition-all border border-line ${className}`}
                    >
                        {showClose && (
                            <button
                                onClick={onClose}
                                className="absolute z-40 top-4 right-4 p-1.5 bg-app-white rounded-full text-app-gray-400 hover:text-app-gray-500 focus:outline-none"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        )}
                        {children}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
} 